import { useCommonEntitiesStore } from "@/States/commonEntities";
import { getWasteTypeName } from "@/Utils/commonExportFunctions";
import { getFlatDownstreamData } from "@/Utils/downstreamHandlingUtils";
import { useConfig } from "@/api/hooks/useConfig";
import InfoPopup from "@/components/InfoPopup/InfoPopup";
import { StyledSelect } from "@/components/StyledSelect";
import { Button } from "@/components/button";
import { Translate, useTrans } from "@/translations";
import { getCurrentLocale } from "@/translations/functions/locale";

import React, { useCallback, useMemo } from "react";
import { SketchPicker } from "react-color";
import { NumericFormat } from "react-number-format";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { filter } from "lodash";
import { ACCESS_POINT } from "server/coreApi/accessPoints/types.js";

import { currencies } from "./payments";
import { WasteTypeData, useConfigService } from "./useConfigService";

type Props = {
  containers: ACCESS_POINT[];
  data: WasteTypeData[];
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  updatedWasteTypes: WasteTypeData[];
  setUpdatedWasteTypes: React.Dispatch<React.SetStateAction<WasteTypeData[]>>;
  hasResetOption?: boolean;
  showCostColumn?: boolean;
};

export const WasteStreamsTable: React.FC<Props> = ({
  containers,
  data,
  setHasUnsavedChanges,
  updatedWasteTypes,
  setUpdatedWasteTypes,
  hasResetOption = false,
  showCostColumn = false,
}) => {
  const { t } = useTrans();
  const {
    languages,
    wasteTypes: sanityWasteTypes,
    downstreamHandling,
  } = useCommonEntitiesStore();
  const { terminalConfig } = useConfigService();
  const { config: clientConfig } = useConfig();

  const flatDownstreamData = useMemo(
    () => getFlatDownstreamData(downstreamHandling || []),
    [downstreamHandling],
  );

  const locale = getCurrentLocale();
  const localeDecimalSeparator = new Intl.NumberFormat(locale)
    .format(1.1)
    .charAt(1);

  const getConfig = useCallback(
    (code?: string) => {
      return (
        updatedWasteTypes.find(
          (wasteType) => wasteType.wasteTypeCode === code,
        ) ?? data?.find((wasteType) => wasteType.wasteTypeCode === code)
      );
    },
    [data, updatedWasteTypes],
  );

  const onDisplayNameChange = useCallback(
    (wasteTypeCode: string, displayName: string, locale: string) => {
      const existing = getConfig(wasteTypeCode);
      const color = existing?.color ?? "";
      const names = existing?.names ?? [];

      const updatedConfig = {
        wasteTypeCode,
        color,
        names: filter(names, (n) => n.locale !== locale).concat({
          displayName,
          locale,
        }),
      };
      setUpdatedWasteTypes((prev) => [
        ...filter(prev, (wt) => wasteTypeCode !== wt.wasteTypeCode),
        updatedConfig,
      ]);
      setHasUnsavedChanges(true);
    },
    [setUpdatedWasteTypes, setHasUnsavedChanges, getConfig],
  );

  const onColorChange = useCallback(
    (wasteTypeCode: string, color: string) => {
      const names = getConfig(wasteTypeCode)?.names ?? [];
      setUpdatedWasteTypes((prev) => [
        ...filter(prev, (wt) => wasteTypeCode !== wt.wasteTypeCode),
        {
          wasteTypeCode,
          color,
          names,
        },
      ]);
      setHasUnsavedChanges(true);
    },
    [setUpdatedWasteTypes, setHasUnsavedChanges, getConfig],
  );

  const resetOption = useMemo(
    () => ({
      label: t("configLabels:sameAsClient"),
      value: "",
    }),
    [t],
  );

  const downstreamOptions = useMemo(() => {
    const options = Object.keys(flatDownstreamData)
      .filter((key) => flatDownstreamData[key].parentIds.length)
      .map((key) => ({
        value: key,
        label: flatDownstreamData[key].name,
      }));

    if (hasResetOption) {
      options.unshift(resetOption);
    }

    return options;
  }, [flatDownstreamData, hasResetOption, resetOption]);

  const onDownstreamHandlingChange = useCallback(
    (wasteTypeCode: string, downstreamHandlingId?: string) => {
      const config = getConfig(wasteTypeCode);
      setUpdatedWasteTypes((prev) => [
        ...filter(prev, (wt) => wasteTypeCode !== wt.wasteTypeCode),
        {
          wasteTypeCode,
          color: config?.color ?? null,
          names: config?.names ?? [],
          co2factor: config?.co2factor || null,
          downstreamHandlingId: downstreamHandlingId || null,
          costs: config?.costs || [],
        },
      ]);
      setHasUnsavedChanges(true);
    },
    [setUpdatedWasteTypes, setHasUnsavedChanges, getConfig],
  );

  const onCo2FactorChange = useCallback(
    (wasteTypeCode: string, co2factor: string) => {
      const config = getConfig(wasteTypeCode);
      setUpdatedWasteTypes((prev) => [
        ...filter(prev, (wt) => wasteTypeCode !== wt.wasteTypeCode),
        {
          wasteTypeCode,
          color: config?.color ?? null,
          names: config?.names ?? [],
          downstreamHandlingId: config?.downstreamHandlingId || null,
          co2factor: Number(co2factor) || null,
          costs: config?.costs || [],
        },
      ]);
      setHasUnsavedChanges(true);
    },
    [setUpdatedWasteTypes, setHasUnsavedChanges, getConfig],
  );

  const onCostChange = useCallback(
    (wasteTypeCode: string, costPerUnit: string) => {
      const storedCosts =
        data?.find((wasteType) => wasteType.wasteTypeCode === wasteTypeCode)
          ?.costs ?? [];
      let previousCost = storedCosts.find((c) => !c.validTo);

      const now = new Date();
      // Update previous cost to validTo now
      if (previousCost) {
        previousCost = {
          ...previousCost,
          validTo: now,
          validFrom: new Date(previousCost.validFrom),
        };
      }

      const newCost = {
        validFrom: now,
        validTo: null,
        costPerUnit: costPerUnit === "" ? null : costPerUnit,
      };

      const updatedCosts = previousCost
        ? [
            // Replace previous cost with validTo now to disable it
            ...storedCosts.filter((c) => !!c.validTo),
            previousCost,
            newCost,
          ]
        : [newCost];

      setUpdatedWasteTypes((prev) => [
        ...filter(prev, (wt) => wasteTypeCode !== wt.wasteTypeCode),
        {
          wasteTypeCode,
          color: getConfig(wasteTypeCode)?.color ?? null,
          names: getConfig(wasteTypeCode)?.names ?? [],
          downstreamHandlingId:
            getConfig(wasteTypeCode)?.downstreamHandlingId || null,
          co2factor: getConfig(wasteTypeCode)?.co2factor || null,
          costs: updatedCosts.filter(
            (c) => c.costPerUnit !== null,
          ) as WasteTypeData["costs"],
        },
      ]);

      setHasUnsavedChanges(true);
    },
    [setUpdatedWasteTypes, setHasUnsavedChanges, getConfig, data],
  );

  return (
    <table className="w-full table-auto text-sm">
      <thead>
        <tr className="h-12 border-b text-left">
          <th>
            <div className="mr-4">{t("configLabels:code")}</div>
          </th>
          <th>
            <div className="ml-2">{t("configLabels:name")}</div>
          </th>
          {languages?.map((language) => (
            <th key={language.code}>
              <div className="ml-2 flex items-center">
                {t("configLabels:displayName")}{" "}
                {t("languages:" + language.code)}
                <InfoPopup
                  id={`configLanguage-${language.code}`}
                  size={15}
                  className="ml-1 inline-block align-baseline"
                  width="350px"
                >
                  <Translate
                    i18nKey={"hints:changeDisplayName"}
                    values={{
                      link: t("sidebarLabels:manageInfrastructure"),
                    }}
                    components={[
                      <a
                        href="/infrastructure/manage"
                        style={{ textDecoration: "underline", color: "#FFF" }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        ${t("sidebarLabels:manageInfrastructure")}
                      </a>,
                    ]}
                  />
                </InfoPopup>
              </div>
            </th>
          ))}
          <th>
            <div className="ml-4 flex items-center">
              {t("configLabels:color")}
              <InfoPopup
                id="configColor"
                size={15}
                className="ml-1 inline-block align-baseline"
                width="350px"
              >
                {t("hints:changeColor")}
              </InfoPopup>
            </div>
          </th>
          <th>
            <div className="ml-4 flex items-center">
              {t("configLabels:downstreamHandling")}
              <InfoPopup
                id="downstreamHandling"
                size={15}
                className="ml-1 inline-block align-baseline"
                width="350px"
              >
                {t("hints:downstreamHandling")}
              </InfoPopup>
            </div>
          </th>
          <th>
            <div className="ml-4 flex items-center">
              {t("configLabels:co2factor")}
              <InfoPopup
                id="co2factor"
                size={15}
                className="ml-1 inline-block align-baseline"
                width="350px"
              >
                {t("hints:co2factor")}
              </InfoPopup>
            </div>
          </th>
          {showCostColumn && (
            <th>
              <div className="ml-4 flex items-center">
                {t("configLabels:pricePerKg")}
                <InfoPopup
                  id="pricePerKg"
                  size={15}
                  className="ml-1 inline-block align-baseline"
                  width="350px"
                >
                  {t("hints:pricePerKg")}
                </InfoPopup>
              </div>
            </th>
          )}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {containers?.map((container, index) => {
          const config = getConfig(container?.wasteType?.code);

          const containerName =
            getWasteTypeName(
              sanityWasteTypes || [],
              container.wasteType?.code,
            ) || container.wasteType.name;

          const rowHasChanges =
            config?.color ||
            !!config?.names?.filter((el) => !!el.displayName).length ||
            !!config?.downstreamHandlingId ||
            !!config?.co2factor;

          return (
            <tr
              key={`${container.id}-${index}`}
              className="h-12 border-b last-of-type:border-none"
            >
              <td>{container.wasteType?.code}</td>
              <td>
                <div className="mx-2">{containerName}</div>
              </td>
              {languages?.map((language) => {
                const nameConfig = config?.names.find(
                  (n) => n.locale === language?.code,
                );
                return (
                  <td key={language.code}>
                    <input
                      className="mx-2 px-2"
                      id={`${container.id}-${language?.code}`}
                      value={nameConfig?.displayName ?? ""}
                      onChange={(e) =>
                        onDisplayNameChange(
                          container.wasteType?.code,
                          e.target.value,
                          language.code,
                        )
                      }
                      data-1p-ignore
                    />
                  </td>
                );
              })}

              <td className="relative">
                <Popover>
                  {({ close }) => (
                    <>
                      <PopoverButton>
                        <div className="mx-4 mt-2 flex min-w-[80px] items-center justify-center">
                          {config?.color ? (
                            <Square color={config.color} />
                          ) : (
                            <div className="-mt-2 cursor-pointer underline">
                              {t("actions:addColor")}
                            </div>
                          )}
                        </div>
                      </PopoverButton>
                      <PopoverPanel className="border-grey3 bg-grey1 absolute bottom-0 z-10 border p-4 shadow-sm">
                        <SketchPicker
                          color={config?.color ?? undefined}
                          onChange={(color) =>
                            onColorChange(container.wasteType?.code, color.hex)
                          }
                          styles={{
                            default: {
                              picker: {
                                boxShadow: "none",
                              },
                            },
                          }}
                        />
                        <Button
                          className="mt-2 w-full"
                          label={t("common:done")}
                          onClick={close}
                        />
                      </PopoverPanel>
                    </>
                  )}
                </Popover>
              </td>

              <td className="px-4">
                <StyledSelect
                  className="my-2"
                  placeholder={
                    downstreamOptions.find(
                      (o: any) =>
                        o.value === config?.downstreamHandlingPlaceholderId,
                    )?.label || ""
                  }
                  options={downstreamOptions}
                  value={downstreamOptions.find(
                    (o: any) => o.value === config?.downstreamHandlingId,
                  )}
                  onChange={(e) =>
                    onDownstreamHandlingChange(
                      container.wasteType?.code,
                      e.value,
                    )
                  }
                />
              </td>
              <td>
                <NumericFormat
                  id={`${container.id}-co2factor`}
                  className="mx-2 flex [appearance:textfield] px-2 text-right text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  value={config?.co2factor?.toString() ?? ""}
                  placeholder={config?.co2Placeholder}
                  onChange={(e) =>
                    onCo2FactorChange(container.wasteType?.code, e.target.value)
                  }
                  decimalSeparator={localeDecimalSeparator}
                  allowedDecimalSeparators={[localeDecimalSeparator, ",", "."]}
                  onKeyDown={(e) =>
                    (e.key === "e" || e.key === "-") && e.preventDefault()
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  data-1p-ignore
                />
              </td>
              {showCostColumn && (
                <td>
                  <NumericFormat
                    id={`${container.id}-price`}
                    className="mx-2 flex [appearance:textfield] px-2 text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    allowNegative
                    value={
                      config?.costs
                        ?.find((c) => !c.validTo)
                        ?.costPerUnit?.toString() ?? ""
                    }
                    onChange={(e) =>
                      onCostChange(container.wasteType?.code, e.target.value)
                    }
                    placeholder={
                      currencies.find(
                        (c) =>
                          c.value === terminalConfig?.currency ||
                          c.value === clientConfig?.currency,
                      )?.label || ""
                    }
                    decimalSeparator={localeDecimalSeparator}
                    allowedDecimalSeparators={[
                      localeDecimalSeparator,
                      ",",
                      ".",
                    ]}
                    onKeyDown={(e) => e.key === "e" && e.preventDefault()}
                    onWheel={(e) => e.currentTarget.blur()}
                    data-1p-ignore
                  />
                </td>
              )}

              <td>
                {rowHasChanges && (
                  <div
                    className="ml-4 cursor-pointer underline"
                    onClick={() => {
                      const { wasteTypeCode } = config;

                      const resetElement = data?.find(
                        (el) => el.wasteTypeCode === wasteTypeCode,
                      );

                      if (resetElement) {
                        setUpdatedWasteTypes((prev) => [
                          ...filter(
                            prev,
                            (wt) => wasteTypeCode !== wt.wasteTypeCode,
                          ),
                          {
                            names: resetElement?.names.map((el) => ({
                              ...el,
                              displayName: "",
                            })),
                            wasteTypeCode,
                            color: "",
                            downstreamHandlingId: "",
                            co2factor: null,
                          },
                        ]);
                        setHasUnsavedChanges(true);
                      } else {
                        setUpdatedWasteTypes((prev) => [
                          ...filter(
                            prev,
                            (wt) => wasteTypeCode !== wt.wasteTypeCode,
                          ),
                          {
                            names: [],
                            wasteTypeCode,
                            color: "",
                            downstreamHandlingId: "",
                            co2factor: null,
                          },
                        ]);
                      }
                    }}
                  >
                    {t("actions:reset")}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Square: React.FC<{ color: string }> = ({ color }) => {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        backgroundColor: color,
      }}
    />
  );
};
